/**
 * Cascata do Esc (§5.6, M21; §15.2.5): UM ouvinte de `keydown` em `window`, fase de captura. A cada Esc, a camada ativa
 * de maior prioridade (menor número) age; o ouvinte chama `preventDefault()` e `stopImmediatePropagation()` (um Esc
 * fecha uma coisa só). Se nenhuma agir, o evento segue sem `preventDefault` (AC-5.6-04).
 * Componentes não escutam Esc: registram uma camada com `useEscapeLayer(priority, isActive, handler)`. As camadas que
 * dependem só de `ui`/`route` (seletor, busca, Sobre, TV, perfil, lugar) nascem em `installEscape(handlers)`, com as
 * ações injetadas pelo App (DE-B17: `a11y/` não importa state/actions.ts).
 * Único arquivo de src/ que compara a tecla Escape (AC-5.6-05).
 */
import { useEffect, useRef } from 'preact/hooks';
import { route, ui } from '../state/store';

/** Prioridades da tabela de §5.6 (menor = age primeiro). */
export const ESC_PRIORITY = Object.freeze({
  /** `MapTooltip` visível: só esconde o tooltip (X2). */
  tooltip: 0,
  candidatePicker: 1,
  search: 2,
  /**
   * O cartão da definição matemática (D4, docs/DECISOES.md §63): modal, fecha antes do Sobre e de tudo o que fica sob
   * ele; a busca, que pode abrir por cima pelo atalho, fecha primeiro.
   */
  mathCard: 2.5,
  about: 3,
  tv: 4,
  profile: 5,
  /** `CandidateListPanel` do arquivo do 1º turno (P2): "5a". */
  candidateList: 5.5,
  /** A tela de comentários (ORD-F-026): volta à Apuração antes de subir de lugar. */
  screen: 5.75,
  zone: 6,
  abroadCity: 7,
  country: 8,
  municipality: 9,
  /** UF ou raiz do Exterior → Brasil do cargo atual. */
  place: 10,
});

export interface EscapeLayer {
  priority: number;
  isActive(): boolean;
  /** Fecha a camada. Devolver `false` = não agiu (a cascata segue para a próxima); qualquer outro valor = agiu. */
  handler(): unknown;
}

interface Registered extends EscapeLayer {
  order: number;
}

const layers: Registered[] = [];
let order = 0;

/** Registra uma camada; devolve o cancelamento. Empate de prioridade: a registrada por último age primeiro. */
export function registerEscapeLayer(layer: EscapeLayer): () => void {
  const entry: Registered = { ...layer, order: ++order };
  layers.push(entry);
  return () => {
    const i = layers.indexOf(entry);
    if (i >= 0) layers.splice(i, 1);
  };
}

/**
 * Roda a cascata uma vez: a camada ativa de menor prioridade age. Devolve true se alguma agiu. Também é o que um
 * `<dialog>` nativo chama no evento `cancel` (depois de cancelá-lo), para delegar ao mesmo fluxo (§5.6).
 */
export function runEscapeCascade(): boolean {
  const sorted = [...layers].sort((a, b) => a.priority - b.priority || b.order - a.order);
  for (const layer of sorted) {
    let active = false;
    try {
      active = layer.isActive();
    } catch {
      active = false;
    }
    if (!active) continue;
    if (layer.handler() === false) continue;
    return true;
  }
  return false;
}

/** Para o evento `cancel` de um `<dialog>` nativo: cancela o fechamento nativo e delega à cascata. */
export function delegateDialogCancel(e: Event): void {
  e.preventDefault();
  runEscapeCascade();
}

/**
 * Camada de Esc de um componente (§5.6). `isActive` pode ser um booleano do render ou uma função; `handler` devolve
 * `false` quando não agiu. A camada vive enquanto o componente estiver montado.
 */
export function useEscapeLayer(priority: number, isActive: boolean | (() => boolean), handler: () => unknown): void {
  const ref = useRef({ isActive, handler });
  ref.current = { isActive, handler };
  useEffect(
    () =>
      registerEscapeLayer({
        priority,
        isActive: () => {
          const a = ref.current.isActive;
          return typeof a === 'function' ? a() : a;
        },
        handler: () => ref.current.handler(),
      }),
    [priority],
  );
}

function isEscapeKey(e: KeyboardEvent): boolean {
  return e.key === 'Escape' || e.key === 'Esc';
}

/** Ações que o App injeta (DE-B17); cada uma presente vira a camada da linha correspondente de §5.6. */
export interface EscapeHandlers {
  /** Linha 1: fecha o `CandidatePicker` (ação `togglePicker(false)`). */
  closePicker?: () => void;
  /** Linha 2: fecha a busca sem navegar (`toggleSearch(false)`). */
  closeSearch?: () => void;
  /** Linha 3: fecha o `AboutDialog` (`closeAbout()`). */
  closeAbout?: () => void;
  /** Linha 4: sai da TV e da tela cheia (`exitTv()`); também ao sair da tela cheia pelo navegador (AC-5.6-06). */
  exitTv?: () => void;
  /** Linha 5: fecha o perfil e restaura o modo do mapa (`closeProfile()`). */
  closeProfile?: () => void;
  /** Linhas 6–10: zona → município, cidade → país ou Exterior, país → Exterior, município → UF, UF/Exterior → Brasil. */
  upOne?: () => void;
  /**
   * Rota em vigor (`currentRoute()` de state/actions.ts: a do push em voo, senão `route`). Sem ela, as camadas de lugar
   * leem `route`, que só muda no `hashchange`: um Esc logo depois de abrir uma UF via o Brasil e não fazia nada
   * (e2e AC-15.4-08 da Fase 5; mesma corrida dos atalhos "2"/"1" da Fase 3).
   */
  currentRoute?: () => typeof route.value; // o tipo vem do store: a11y não importa model/ (§2.2.2)
}

interface FullscreenDoc {
  fullscreenElement?: Element | null;
  webkitFullscreenElement?: Element | null;
}

function isFullscreen(doc: Document): boolean {
  const d = doc as unknown as FullscreenDoc;
  return Boolean(d.fullscreenElement ?? d.webkitFullscreenElement);
}

let uninstallCurrent: (() => void) | null = null;

/**
 * Instala o ouvinte único (captura em `window`) e as camadas das ações injetadas. Chamar de novo reinstala.
 * Devolve o desligamento.
 */
export function installEscape(h: EscapeHandlers, win: Window = window): () => void {
  uninstallCurrent?.();
  const off: Array<() => void> = [];
  const add = (priority: number, isActive: () => boolean, fn: (() => void) | undefined) => {
    if (!fn) return;
    off.push(
      registerEscapeLayer({
        priority,
        isActive,
        handler: () => {
          fn();
          return true;
        },
      }),
    );
  };
  const place = () => (h.currentRoute?.() ?? route.value).place;
  const kind = () => place().kind;
  const hasZone = () => {
    const p = place();
    return p.kind === 'municipality' && p.zone !== null;
  };
  add(ESC_PRIORITY.candidatePicker, () => ui.value.pickerOpen, h.closePicker);
  add(ESC_PRIORITY.search, () => ui.value.searchOpen, h.closeSearch);
  add(ESC_PRIORITY.about, () => ui.value.aboutOpen !== null, h.closeAbout);
  add(ESC_PRIORITY.tv, () => ui.value.tv.on, h.exitTv);
  add(ESC_PRIORITY.profile, () => ui.value.profile !== null, h.closeProfile);
  add(ESC_PRIORITY.zone, hasZone, h.upOne);
  add(ESC_PRIORITY.abroadCity, () => kind() === 'abroad_city', h.upOne);
  add(ESC_PRIORITY.country, () => kind() === 'country', h.upOne);
  add(ESC_PRIORITY.municipality, () => kind() === 'municipality', h.upOne);
  add(ESC_PRIORITY.place, () => kind() === 'uf' || kind() === 'abroad', h.upOne);

  const onKeyDown = (e: KeyboardEvent) => {
    if (!isEscapeKey(e) || e.defaultPrevented || e.isComposing || e.keyCode === 229) return;
    if (runEscapeCascade()) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  };
  win.addEventListener('keydown', onKeyDown, true);
  off.push(() => win.removeEventListener('keydown', onKeyDown, true));

  // Tela cheia real: o navegador consome o Esc e pode não entregar o keydown; a saída da TV escuta a troca (§5.6).
  const doc = win.document;
  let wasFull = isFullscreen(doc);
  const onFullscreen = () => {
    const full = isFullscreen(doc);
    if (wasFull && !full && ui.value.tv.on) h.exitTv?.();
    wasFull = full;
  };
  doc.addEventListener('fullscreenchange', onFullscreen);
  doc.addEventListener('webkitfullscreenchange', onFullscreen);
  off.push(() => {
    doc.removeEventListener('fullscreenchange', onFullscreen);
    doc.removeEventListener('webkitfullscreenchange', onFullscreen);
  });

  const uninstall = () => {
    for (const f of off.splice(0)) f();
    if (uninstallCurrent === uninstall) uninstallCurrent = null;
  };
  uninstallCurrent = uninstall;
  return uninstall;
}
